/**
 * useLiveSearch - Hook for real-time search with debounce and state management
 *
 * Расширено:
 * - Если товары не найдены, выполняется fallback-поиск по категориям
 * - Категория возвращается как отдельный тип результата
 */

import { useState, useCallback, useRef, useEffect, useMemo } from "react";

import { SearchStatus, MIN_QUERY_LENGTH, DEBOUNCE_MS, UseLiveSearchReturn, MeilisearchSearchResponse } from "../types/api";
import { SearchResults } from "../search-core/types/SearchResults";

import { SearchOptions, SearchService } from "../types";
import { createSearchService } from "../services";
import { searchMapper } from "../search-core/searchMapper";
import { categorySource } from "../search-core/categories/categorySource";
import { getSearchHistory, filterHistoryByQuery } from "../search-core/history";
import { generateSuggestions } from "../search-core/suggestions";
import { isBitrixMode, buildBitrixApiUrl } from "../utils/bitrixApiUrl";

const SEARCHBOOSTER_LIMIT = 100; // Should match backend SEARCHBOOSTER_LIMIT environment variable

/* ===========================================================================
 * Опции хука
 * =========================================================================== */

export interface UseLiveSearchOptions {
	initialQuery?: string;
	serviceType?: "mock" | "api" | "meilisearch";
	serviceConfig?: Record<string, unknown>;
	debounceDelay?: number;
	autoSearch?: boolean;
	searchOptions?: SearchOptions;
}

/* ===========================================================================
 * Основной хук
 * =========================================================================== */

export function useLiveSearch(options: UseLiveSearchOptions = {}): UseLiveSearchReturn {
	const { initialQuery = "", serviceType = "api", serviceConfig = {}, debounceDelay = DEBOUNCE_MS, autoSearch = true, searchOptions = {} } = options;
	// Use configured baseUrl, fallback to window.LiveSearchConfig, env variable, or default Bitrix path
	let baseUrl: string;
	const configuredBaseUrl = serviceConfig.baseUrl;
	if (typeof configuredBaseUrl === "string" && configuredBaseUrl) {
		baseUrl = configuredBaseUrl;
	} else if (typeof window !== "undefined" && window.LiveSearchConfig?.apiBasePath) {
		baseUrl = window.LiveSearchConfig.apiBasePath;
	} else if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) {
		baseUrl = import.meta.env.VITE_API_BASE_URL;
	} else {
		baseUrl = "/local/modules/live_search/api/";
	}
	// Ensure no trailing slash for consistency
	if (baseUrl.endsWith("/")) {
		baseUrl = baseUrl.slice(0, -1);
	}

	/* -----------------------------------------------------------------------
	 * State
	 * --------------------------------------------------------------------- */

	const [query, setQueryState] = useState(initialQuery);
	const [results, setResults] = useState<unknown[]>([]);
	const [status, setStatus] = useState<SearchStatus>("idle");
	const [error, setError] = useState<string | null>(null);
	const [rawResponse, setRawResponse] = useState<MeilisearchSearchResponse | null>(null);
	const [hasMore, setHasMore] = useState(false);

	/* -----------------------------------------------------------------------
	 * Refs
	 * --------------------------------------------------------------------- */

	const searchServiceRef = useRef<SearchService | null>(null);
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastQueryRef = useRef<string>("");
	// Stable data refs to prevent UI flicker during typing/loading
	const lastStableNormalizedResultsRef = useRef<SearchResults | null>(null);
	const lastStableSuggestionsRef = useRef<string[]>([]);
	// Last query that was counted for popular queries increment
	const lastCountedQueryRef = useRef<string>("");
	// Accumulated search results for pagination (SearchBooster)
	const accumulatedHitsRef = useRef<unknown[]>([]);
	const accumulatedTotalHitsRef = useRef<number>(0);
	const uiVisibleCountRef = useRef<number>(10);
	const engineOffsetRef = useRef<number>(0);

	/* -----------------------------------------------------------------------
	 * Init search service + preload categories
	 * --------------------------------------------------------------------- */

	useEffect(() => {
		searchServiceRef.current = createSearchService(serviceType, serviceConfig);

		searchServiceRef.current.initialize(serviceConfig).catch((err) => {
			console.error("Failed to initialize search service:", err);
		});

		// preload категорий (dev / feed)
		categorySource.preload().catch((err) => {
			console.warn("Failed to preload categories:", err);
		});

		return () => {
			searchServiceRef.current?.cancel?.();
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, [serviceType, serviceConfig]);

	/* -----------------------------------------------------------------------
	 * Helpers
	 * --------------------------------------------------------------------- */

	const validateQuery = useCallback((value: string): boolean => {
		const trimmed = value.trim();
		if (!trimmed) return false;
		if (trimmed.length < MIN_QUERY_LENGTH) return false;
		return true;
	}, []);

	const cancelSearch = useCallback(() => {
		searchServiceRef.current?.cancel?.();
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
			debounceTimeoutRef.current = null;
		}
		if (status === "loading") {
			setStatus("idle");
		}
	}, [status]);

	const clearSearch = useCallback(() => {
		cancelSearch();
		setQueryState("");
		setResults([]);
		setStatus("idle");
		setError(null);
		setRawResponse(null);
		lastQueryRef.current = "";
		// Clear stable data refs
		lastStableNormalizedResultsRef.current = null;
		lastStableSuggestionsRef.current = [];
		// Clear last counted query
		lastCountedQueryRef.current = "";
	}, [cancelSearch]);

	/**
	 * Increment popular query count on server
	 */
	const incrementPopularQuery = useCallback(
		async (queryToIncrement: string) => {
			if (!queryToIncrement || queryToIncrement.trim().length < 2) {
				return;
			}

			try {
				const endpoint = isBitrixMode() ? buildBitrixApiUrl("/api/popular-queries/increment") : `${baseUrl}/api/popular-queries/increment`;
				const response = await fetch(endpoint, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ query: queryToIncrement }),
				});

				if (!response.ok) {
					console.error("Failed to increment popular query:", response.status);
				}
				// Ignore response, this is a background operation
			} catch (error) {
				// Silent fail - background operation
				console.error("Error incrementing popular query:", error);
			}
		},
		[baseUrl],
	);

	/* -----------------------------------------------------------------------
	 * Perform search
	 * --------------------------------------------------------------------- */

	const performSearch = useCallback(
		async (searchQuery: string, overrideOptions?: SearchOptions) => {
			setStatus("loading");
			setError(null);

			const isNewQuery = lastQueryRef.current !== searchQuery;

			// ------------------------------------------------------------
			// СБРОС СОСТОЯНИЯ ПРИ НОВОМ ЗАПРОСЕ
			// ------------------------------------------------------------
			if (isNewQuery) {
				accumulatedHitsRef.current = [];
				accumulatedTotalHitsRef.current = 0;

				uiVisibleCountRef.current = 10;
				engineOffsetRef.current = 0;

				setHasMore(false);
			}

			lastQueryRef.current = searchQuery;

			try {
				if (!searchServiceRef.current) {
					throw new Error("Search service not initialized");
				}

				const baseOptions = { ...searchOptions, ...overrideOptions };

				const offset = typeof baseOptions.offset === "number" ? baseOptions.offset : engineOffsetRef.current;

				const limit = typeof baseOptions.limit === "number" ? baseOptions.limit : SEARCHBOOSTER_LIMIT;

				const response = await searchServiceRef.current.search(searchQuery, {
					...baseOptions,
					limit,
					offset,
				});

				// ------------------------------------------------------------
				// ОБНОВЛЕНИЕ НАКОПЛЕННЫХ ДАННЫХ
				// ------------------------------------------------------------
				if (offset === 0) {
					accumulatedHitsRef.current = response.results.map((r) => r.metadata || r);
				} else {
					accumulatedHitsRef.current = [...accumulatedHitsRef.current, ...response.results.map((r) => r.metadata || r)];
				}

				accumulatedTotalHitsRef.current = response.total;

				// ------------------------------------------------------------
				// UI-СЛАЙС (ПОКАЗЫВАЕМ ТОЛЬКО N)
				// ------------------------------------------------------------
				const visibleHits = uiVisibleCountRef.current >= accumulatedHitsRef.current.length ? accumulatedHitsRef.current : accumulatedHitsRef.current.slice(0, uiVisibleCountRef.current);

				// ------------------------------------------------------------
				// ФОРМИРУЕМ ЕДИНСТВЕННЫЙ ИСТОЧНИК ДЛЯ UI
				// ------------------------------------------------------------
				const nextRawResponse: MeilisearchSearchResponse = {
					hits: visibleHits,
					query: searchQuery,
					processingTimeMs: response.took,
					limit,
					offset,
					estimatedTotalHits: accumulatedTotalHitsRef.current,
					facets: response.facets || undefined,
				};

				setRawResponse(nextRawResponse);

				const hasMoreEngine = accumulatedHitsRef.current.length < accumulatedTotalHitsRef.current;
				console.log("hasMoreEngine:", hasMoreEngine, {
					accumulatedHits: accumulatedHitsRef.current.length,
					accumulatedTotalHits: accumulatedTotalHitsRef.current,
				});

				setHasMore(hasMoreEngine);

				setStatus(visibleHits.length > 0 ? "success" : "empty");
			} catch (err) {
				const message = err instanceof Error ? err.message : "Unknown search error";

				setError(message);
				setStatus("error");
				setRawResponse(null);
			}
		},
		[searchOptions],
	);

	/* -----------------------------------------------------------------------
	 * Load more results (pagination)
	 * --------------------------------------------------------------------- */

	const loadMore = useCallback(() => {
		const currentQuery = lastQueryRef.current;
		if (!currentQuery) return;

		const totalFetched = accumulatedHitsRef.current.length;
		const totalAvailable = accumulatedTotalHitsRef.current;

		console.log("loadMore (force full):", {
			totalFetched,
			totalAvailable,
		});

		// Если уже всё загружено — просто показываем всё
		if (totalFetched >= totalAvailable) {
			uiVisibleCountRef.current = totalAvailable;

			setRawResponse((prev) => (prev ? { ...prev, hits: accumulatedHitsRef.current } : prev));

			setHasMore(false);
			return;
		}
		uiVisibleCountRef.current = totalAvailable;

		// ДОГРУЖАЕМ ВСЁ ОСТАЛЬНОЕ
		performSearch(currentQuery, {
			offset: totalFetched,
			limit: totalAvailable,
		});
	}, [performSearch]);

	/* -----------------------------------------------------------------------
	 * setQuery with debounce
	 * --------------------------------------------------------------------- */

	const setQuery = useCallback(
		(newQuery: string) => {
			setQueryState(newQuery);

			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
				debounceTimeoutRef.current = null;
			}

			const trimmed = newQuery.trim();

			if (!trimmed) {
				clearSearch();
				return;
			}

			if (!validateQuery(trimmed)) {
				setStatus("idle");
				return;
			}

			setStatus("typing");

			if (!autoSearch) return;

			debounceTimeoutRef.current = setTimeout(() => {
				debounceTimeoutRef.current = null;
				if (validateQuery(trimmed)) {
					performSearch(trimmed);
				} else {
					setStatus("idle");
				}
			}, debounceDelay);
		},
		[autoSearch, debounceDelay, validateQuery, performSearch, clearSearch],
	);

	/* -----------------------------------------------------------------------
	 * Manual search
	 * --------------------------------------------------------------------- */

	const search = useCallback(
		async (value: string) => {
			const trimmed = value.trim();

			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
				debounceTimeoutRef.current = null;
			}

			cancelSearch();
			setQueryState(trimmed);

			if (!validateQuery(trimmed)) {
				if (!trimmed) clearSearch();
				else setStatus("idle");
				return;
			}

			await performSearch(trimmed);
		},
		[cancelSearch, validateQuery, performSearch, clearSearch],
	);

	/* -----------------------------------------------------------------------
	 * Derived state
	 * --------------------------------------------------------------------- */

	const isLoading = status === "loading";
	const isSuccess = status === "success";
	const isEmpty = status === "empty";
	const hasError = status === "error";

	const normalizedResults = useMemo(() => {
		if (!rawResponse) return null;
		try {
			return searchMapper(rawResponse, rawResponse.query);
		} catch (err) {
			console.error("searchMapper error:", err);
			return null;
		}
	}, [rawResponse]);

	// Search history
	const history = getSearchHistory();
	const filteredHistory = filterHistoryByQuery(query);

	/* -----------------------------------------------------------------------
	 * Stable data management (prevents UI flicker during typing/loading)
	 * --------------------------------------------------------------------- */

	// Stable versions of data for UI - show previous results during typing/loading
	const stableNormalizedResults = useMemo(() => {
		if (status === "success" || status === "empty") {
			return normalizedResults;
		}
		// During typing/loading, show last stable results
		return lastStableNormalizedResultsRef.current;
	}, [status, normalizedResults]);

	// Update stable refs when we have successful results
	useEffect(() => {
		if (status === "success" || status === "empty") {
			lastStableNormalizedResultsRef.current = normalizedResults;
		}
	}, [status, normalizedResults]);

	// Search suggestions (depends on stable normalized results)
	const suggestions = useMemo(() => {
		if (!query || query.trim().length < 2) {
			return [];
		}

		return generateSuggestions(
			{
				query: query.trim(),
				historyItems: history,
				categories: stableNormalizedResults?.categories || [],
				products: stableNormalizedResults?.products || [],
			},
			{ maxSuggestions: 5 },
		);
	}, [query, history, stableNormalizedResults]);

	// Stable suggestions - show previous suggestions during typing/loading
	const stableSuggestions = useMemo(() => {
		if (status === "success" || status === "empty") {
			return suggestions;
		}
		// During typing/loading, show last stable suggestions
		return lastStableSuggestionsRef.current;
	}, [status, suggestions]);

	// Update stable suggestions ref when we have successful results
	useEffect(() => {
		if (status === "success" || status === "empty") {
			lastStableSuggestionsRef.current = suggestions;
		}
	}, [status, suggestions]);

	// Increment popular query count when search completes successfully
	useEffect(() => {
		if (status === "success" || status === "empty") {
			const trimmedQuery = query.trim();

			// Conditions for increment:
			// 1. Query length >= 2 characters
			// 2. Query different from last counted query
			// 3. Not already counted in this session
			if (trimmedQuery.length >= 2 && trimmedQuery !== lastCountedQueryRef.current) {
				// Update ref first to prevent duplicate increments
				lastCountedQueryRef.current = trimmedQuery;
				// Call increment in background
				incrementPopularQuery(trimmedQuery);
			}
		}
	}, [status, query, incrementPopularQuery]);

	/* -----------------------------------------------------------------------
	 * Public API
	 * --------------------------------------------------------------------- */

	return {
		query,
		results,
		status,
		error,
		rawResponse,
		normalizedResults: stableNormalizedResults,

		history,
		filteredHistory,
		suggestions: stableSuggestions,

		setQuery,
		search,
		cancel: cancelSearch,
		clear: clearSearch,
		loadMore,
		hasMore,

		isLoading,
		isSuccess,
		isEmpty,
		hasError,
	};
}

export default useLiveSearch;
